import { flexRender } from '@tanstack/react-table';
import { useTable } from '../TableContext';
import { cn } from '../../../../utils/cn';
import {
  BarsArrowUpIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';

const SortIcon = ({ isSorted }: { isSorted: false | 'asc' | 'desc' }) => {
  if (!isSorted) {
    return null;
  }

  return (
    <span
      className={cn(
        'inline-flex',
        'transition-opacity duration-150',
        'group-hover:opacity-100',
        !isSorted && 'opacity-0',
      )}
    >
      {isSorted === 'desc' ? (
        <BarsArrowDownIcon className="h-4 w-4" />
      ) : (
        <BarsArrowUpIcon className="h-4 w-4" />
      )}
    </span>
  );
};

const getHeaderCellClasses = (
  variant: 'nested' | 'rounded' | 'outlined' | 'contained',
  isFirst: boolean,
  isLast: boolean,
  canSort: boolean,
  horizontalDensity: 'default' | 'compact',
) =>
  cn(
    horizontalDensity === 'compact' ? 'px-3 py-4' : 'px-5 py-4',
    'text-left',
    'transition duration-150',
    'group relative',
    'border-table-border',
    variant === 'contained'
      ? [
          'bg-gray-50 dark:bg-gray-800',
          'font-medium text-gray-600 dark:text-gray-400',
          // The frame owns the top edge; border-y here would double it.
          'border-b',
        ]
      : [
          'bg-white dark:bg-gray-900',
          'font-normal',
          variant === 'outlined' ? 'border-b' : 'border-y',
        ],
    variant === 'rounded' && [
      isFirst && 'rounded-l-2xl',
      isLast && 'rounded-r-2xl',
    ],
    variant === 'rounded' && [isFirst && 'border-l', isLast && 'border-r'],
    // Hover sits one shade past each variant's resting tint.
    canSort &&
      (variant === 'contained'
        ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
        : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800'),
  );

// Main TableHeader component
interface TableHeaderProps {
  className?: string;
}

export function TableHeader({ className }: TableHeaderProps) {
  const {
    table,
    variant = 'nested',
    horizontalDensity = 'default',
    header,
  } = useTable();

  return (
    <thead className={cn(header?.isSticky && 'sticky top-0 z-10', className)}>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => {
            const isFirst = index === 0;
            const isLast = index === headerGroup.headers.length - 1;
            const isSorted = header.column.getIsSorted();
            const canSort = header.column.getCanSort();

            return (
              <th
                key={header.id}
                role="columnheader"
                aria-sort={
                  canSort
                    ? isSorted === 'asc'
                      ? 'ascending'
                      : isSorted === 'desc'
                        ? 'descending'
                        : 'none'
                    : undefined
                }
                className={getHeaderCellClasses(
                  variant,
                  isFirst,
                  isLast,
                  canSort,
                  horizontalDensity,
                )}
                onClick={header.column.getToggleSortingHandler()}
              >
                <div className="flex items-center gap-2">
                  <span>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </span>
                  {canSort && <SortIcon isSorted={isSorted} />}
                </div>
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

TableHeader.displayName = 'TableHeader';
